import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Badge, Button, Pagination, Form, Spinner, Modal, Tabs, Tab, Dropdown } from 'react-bootstrap';
import { FaSearch, FaEye, FaFilter, FaSignOutAlt, FaUser, FaBell, FaQuestionCircle, FaTimes } from 'react-icons/fa';
import WFBackofficeSidebar from '../../components/WFBackofficeSidebar';
import useDebounce from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/useAuth';
import { notificationApi, searchApi, wfbackofficeApi } from '../../services/api';
import SearchResults from '../../components/SearchResults';
import './Dashboard.css';

const WFBackofficeAgents = () => {
  const navigate = useNavigate();
  const { logout, getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();
  
  // --- Notification State ---
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  // --- End Notification State ---
  
  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);
  // --- End Search State ---

  // Agent state
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 10;
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState('');
  const [dsoFilter, setDsoFilter] = useState('');
  const [dsoList, setDsoList] = useState([]);
  const [loadingDsos, setLoadingDsos] = useState(false);
  
  // Modal state for agent details
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  // Set document title on mount
  useEffect(() => {
    document.title = 'Wishfin Backoffice - Agents';
  }, []);

  // Handle search input changes with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        performSearch(query);
      }, 300); // 300ms debounce
      
      setSearchTimeout(timeoutId);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };
  
  // Perform the search
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle search result click
  const handleResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
    setSearchQuery('');
  };
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchRef]);
  
  // Fetch DSO list for filtering
  useEffect(() => {
    const fetchDsoList = async () => {
      setLoadingDsos(true);
      try {
        const response = await wfbackofficeApi.getDsoPartners({ limit: 100 });
        setDsoList(response.data.items || []);
      } catch (error) {
        console.error('Error fetching DSO list:', error);
        setDsoList([]);
      } finally {
        setLoadingDsos(false);
      }
    };

    fetchDsoList();
  }, []);

  // Fetch agents
  useEffect(() => {
    const fetchAgents = async () => {
      setLoading(true);
      try {
        // Use WFBackoffice API endpoint for agents
        const response = await wfbackofficeApi.getAgents({
          skip: (currentPage - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          search: debouncedSearchTerm,
          status: statusFilter !== '' ? statusFilter : undefined,
          dso_id: dsoFilter !== '' ? dsoFilter : undefined
        });
        
        if (response && response.data) {
          setAgents(response.data.items || []);
          setTotalPages(Math.ceil(response.data.total / PAGE_SIZE) || 1);
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
        // Fallback to mock data if API fails
        const mockAgents = [
          {
            id: 1,
            name: "John Smith",
            email: "john.smith@example.com",
            phone: "9876543210",
            dso_name: "ABC Finance Partners",
            status: "active",
            created_at: "2024-08-05",
            lead_count: 32
          },
          {
            id: 2,
            name: "Priya Sharma",
            email: "priya.sharma@example.com",
            phone: "8765432109",
            dso_name: "XYZ Loan Services",
            status: "pending",
            created_at: "2024-07-12",
            lead_count: 18
          },
          {
            id: 3,
            name: "Michael Johnson",
            email: "michael.j@example.com",
            phone: "7654321098",
            dso_name: "Horizon Finance",
            status: "active",
            created_at: "2024-09-03",
            lead_count: 47
          }
        ];
        setAgents(mockAgents);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [currentPage, debouncedSearchTerm, statusFilter, dsoFilter]);

  // --- Notification Helper Functions ---
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      // Update local state optimistically
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return; // No unread notifications

    try {
      await notificationApi.markAllAsRead();
      // Update local state optimistically
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0); // Reset count
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) {
      console.error("Error formatting timestamp:", e);
      return timestamp;
    }
  };
  
  // Logout handler
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle agent modal open
  const handleViewAgent = async (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    if (agent) {
      setSelectedAgent(agent);
      setShowAgentModal(true);
      setLoadingDetails(true);
      
      try {
        // Fetch detailed information about the agent using the WFBackoffice API
        const response = await wfbackofficeApi.getAgentDetails(agentId);
        setAgentDetails(response.data);
      } catch (error) {
        console.error('Error fetching agent details:', error);
        // Use basic agent info as fallback if API call fails
        setAgentDetails({
          ...agent,
          address: agent.address || 'N/A',
          city: agent.city || 'N/A',
          state: agent.state || 'N/A',
          id_verified: true,
          id_uploaded_at: agent.created_at,
          address_verified: false,
          total_leads: agent.lead_count || 0,
          converted_leads: Math.round((agent.lead_count || 0) * 0.6),
          total_earnings: (agent.lead_count || 0) * 1500,
          recent_activities: [
            {
              timestamp: new Date().toISOString(),
              type: "Lead Added",
              details: "Added new lead for Personal Loan"
            }
          ]
        });
      } finally {
        setLoadingDetails(false);
      }
    }
  };
  
  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate pagination items
  const generatePaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    items.push(
      <Pagination.Prev 
        key="prev" 
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      />
    );
    
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }
    
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item 
          key={page} 
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item 
          key={totalPages} 
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    items.push(
      <Pagination.Next 
        key="next" 
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      />
    );
    
    return items;
  };

  // Get badge color based on status
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="backoffice-dashboard-layout">
      <WFBackofficeSidebar />
      
      <div className="backoffice-content-wrapper">
        <header className="backoffice-header">
          <div className="backoffice-greeting">
            <span className="backoffice-greeting-text">Hi, {currentUser?.name || 'Wishfin'} <span className="backoffice-greeting-emoji">👋</span></span>
          </div>
          <div className="backoffice-header-search" ref={searchRef}>
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search agents..." 
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={() => {
                  if (searchQuery.trim().length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
              {searchQuery && !isSearching && (
                <FaTimes 
                  className="search-clear-icon" 
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                    setSearchResults([]);
                  }} 
                />
              )}
            </div>
            
            {/* Desktop Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-container">
                <SearchResults results={searchResults} onResultClick={handleResultClick} />
              </div>
            )}
            
            {/* Mobile Search Button */}
            <div className="mobile-search-button" onClick={() => setShowSearchModal(true)}>
              <FaSearch />
            </div>
          </div>
          
          <div className="backoffice-header-actions">
            <div className="backoffice-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            {/* Notification Dropdown */}
            <Dropdown
              className="notification-dropdown backoffice-notifications"
              show={showNotifications}
              onToggle={(isOpen) => setShowNotifications(isOpen)}
              align="end"
            >
              <Dropdown.Toggle variant="link" id="dropdown-notifications" className="p-0 border-0 backoffice-header-icon">
                <FaBell size={18} />
                {unreadCount > 0 && (
                  <Badge pill bg="danger" className="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ minWidth: '320px', maxHeight: '450px', overflowY: 'auto', padding: '0' }}>
                <Dropdown.Header className="d-flex justify-content-between align-items-center sticky-top bg-light">
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <button className="btn btn-link btn-sm p-0" onClick={handleMarkAllAsRead}>
                      Mark all as read
                    </button>
                  )}
                </Dropdown.Header>
                {loadingNotifications ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <Dropdown.Item
                      key={notification.id}
                      className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="notification-content">
                          <div className="notification-title">{notification.title || notification.type}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-timestamp">{formatTimestamp(notification.created_at)}</div>
                        </div>
                        {!notification.is_read && <div className="unread-dot"></div>}
                      </div>
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-4">No new notifications</Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </Dropdown>
            <div className="backoffice-user-profile">
              <div className="backoffice-avatar">
                <FaUser />
              </div>
              <div className="backoffice-user-info">
                <span className="backoffice-user-name">
                  {currentUser?.name || 'Wishfin User'}
                </span>
                <button className="backoffice-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="backoffice-main-content">
          <div className="backoffice-dashboard-welcome">
            <h1>Agent Management</h1>
            <p>View and manage agents across all DSO partners</p>
          </div>
          
          <div className="filter-container mb-4">
            <div className="row">
              <div className="col-md-4 mb-3 mb-md-0">
                <div className="search-input-container">
                  <FaSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Search by name, phone, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3 mb-3 mb-md-0">
                <Form.Select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Select 
                  value={dsoFilter} 
                  onChange={(e) => setDsoFilter(e.target.value)}
                  aria-label="Filter by DSO"
                  disabled={loadingDsos}
                >
                  <option value="">All DSOs</option>
                  {dsoList.map(dso => (
                    <option key={dso.id} value={dso.id}>{dso.name}</option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading agents...</p>
            </div>
          ) : agents.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table striped hover className="backoffice-table">
                  <thead>
                    <tr>
                      <th>Agent ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>DSO Partner</th>
                      <th>Status</th>
                      <th>Leads</th>
                      <th>Registration Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent) => (
                      <tr key={agent.id}>
                        <td>{agent.id}</td>
                        <td>{agent.name}</td>
                        <td>{agent.phone}</td>
                        <td>{agent.dso_name || 'N/A'}</td>
                        <td>
                          <Badge bg={getStatusBadge(agent.status)}>
                            {agent.status || 'Unknown'}
                          </Badge>
                        </td>
                        <td>{agent.lead_count || 0}</td>
                        <td>
                          {agent.created_at 
                            ? new Date(agent.created_at).toLocaleDateString() 
                            : 'N/A'}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewAgent(agent.id)}
                            className="action-btn"
                          >
                            <FaEye /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              <div className="d-flex justify-content-between align-items-center mt-4 pagination-container">
                <div>
                  Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, agents.length)} to {Math.min(currentPage * PAGE_SIZE, agents.length)} of {totalPages * PAGE_SIZE} entries
                </div>
                <Pagination>{generatePaginationItems()}</Pagination>
              </div>
            </>
          ) : (
            <div className="text-center my-5">
              <p>No agents found matching your criteria.</p>
              {(debouncedSearchTerm || statusFilter || dsoFilter) && (
                <Button 
                  variant="link" 
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                    setDsoFilter('');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Agent Details Modal */}
      <Modal 
        show={showAgentModal} 
        onHide={() => setShowAgentModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Agent Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center my-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading details...</p>
            </div>
          ) : selectedAgent && agentDetails ? (
            <div className="agent-details">
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
              >
                <Tab eventKey="profile" title="Profile">
                  <div className="row">
                    <div className="col-md-6">
                      <h5>Basic Information</h5>
                      <table className="table table-borderless">
                        <tbody>
                          <tr>
                            <th>Agent ID</th>
                            <td>{selectedAgent.id}</td>
                          </tr>
                          <tr>
                            <th>Name</th>
                            <td>{selectedAgent.name}</td>
                          </tr>
                          <tr>
                            <th>Phone</th>
                            <td>{selectedAgent.phone}</td>
                          </tr>
                          <tr>
                            <th>Email</th>
                            <td>{selectedAgent.email}</td>
                          </tr>
                          <tr>
                            <th>DSO Partner</th>
                            <td>{selectedAgent.dso_name || 'N/A'}</td>
                          </tr>
                          <tr>
                            <th>Status</th>
                            <td>
                              <Badge bg={getStatusBadge(selectedAgent.status)}>
                                {selectedAgent.status || 'Unknown'}
                              </Badge>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="col-md-6">
                      <h5>Additional Information</h5>
                      <table className="table table-borderless">
                        <tbody>
                          <tr>
                            <th>Registration Date</th>
                            <td>
                              {selectedAgent.created_at 
                                ? new Date(selectedAgent.created_at).toLocaleDateString() 
                                : 'N/A'}
                            </td>
                          </tr>
                          <tr>
                            <th>Address</th>
                            <td>{agentDetails.address || 'No address provided'}</td>
                          </tr>
                          <tr>
                            <th>City</th>
                            <td>{agentDetails.city || 'N/A'}</td>
                          </tr>
                          <tr>
                            <th>State</th>
                            <td>{agentDetails.state || 'N/A'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Tab>
                <Tab eventKey="performance" title="Performance">
                  <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h6>Total Leads</h6>
                          <h3>{agentDetails.total_leads || 0}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h6>Converted Leads</h6>
                          <h3>{agentDetails.converted_leads || 0}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h6>Conversion Rate</h6>
                          <h3>
                            {agentDetails.total_leads 
                              ? Math.round((agentDetails.converted_leads / agentDetails.total_leads) * 100) 
                              : 0}%
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <h6>Total Earnings</h6>
                          <h3>₹{agentDetails.total_earnings?.toLocaleString() || 0}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <h5>Recent Activity</h5>
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Activity</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentDetails.recent_activities?.length > 0 ? (
                          agentDetails.recent_activities.map((activity, index) => (
                            <tr key={index}>
                              <td>{new Date(activity.timestamp).toLocaleDateString()}</td>
                              <td>{activity.type}</td>
                              <td>{activity.details}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center">No recent activity</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab>
                <Tab eventKey="documents" title="Documents">
                  <div className="row">
                    <div className="col-12">
                      <h5>Verification Documents</h5>
                      <Table striped hover>
                        <thead>
                          <tr>
                            <th>Document Type</th>
                            <th>Status</th>
                            <th>Uploaded On</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>ID Proof</td>
                            <td>
                              <Badge bg={agentDetails.id_verified ? 'success' : 'warning'}>
                                {agentDetails.id_verified ? 'Verified' : 'Pending'}
                              </Badge>
                            </td>
                            <td>
                              {agentDetails.id_uploaded_at 
                                ? new Date(agentDetails.id_uploaded_at).toLocaleDateString() 
                                : 'Not uploaded'}
                            </td>
                            <td>
                              {agentDetails.id_document_url && (
                                <Button variant="outline-primary" size="sm">
                                  View
                                </Button>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Address Proof</td>
                            <td>
                              <Badge bg={agentDetails.address_verified ? 'success' : 'warning'}>
                                {agentDetails.address_verified ? 'Verified' : 'Pending'}
                              </Badge>
                            </td>
                            <td>
                              {agentDetails.address_uploaded_at 
                                ? new Date(agentDetails.address_uploaded_at).toLocaleDateString() 
                                : 'Not uploaded'}
                            </td>
                            <td>
                              {agentDetails.address_document_url && (
                                <Button variant="outline-primary" size="sm">
                                  View
                                </Button>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>
              </Tabs>
            </div>
          ) : (
            <p className="text-center">No agent details available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAgentModal(false)}>
            Close
          </Button>
          <Button 
            variant="primary" 
            disabled={!selectedAgent}
            onClick={() => {
              // Handle view complete profile action
              if (selectedAgent) {
                // Navigate or perform action
                console.log(`View complete profile for agent ${selectedAgent.id}`);
                setShowAgentModal(false);
              }
            }}
          >
            View Complete Profile
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Mobile Search Modal */}
      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered
        className="search-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mobile-search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search agents..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
            {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
            {searchQuery && !isSearching && (
              <FaTimes
                className="search-clear-icon"
                onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                  setSearchResults([]);
                }}
              />
            )}
          </div>
          
          {searchResults.length > 0 && (
            <div className="mobile-search-results">
              <SearchResults results={searchResults} onResultClick={handleResultClick} />
            </div>
          )}
          
          {searchQuery.trim().length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="no-results-message">
              No results found for "{searchQuery}"
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WFBackofficeAgents;